import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Row, Col, Button, Space, Spin, Steps, message } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined, StopOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { getDetailedLoanInfo, updateLoan, clearLoanCache, deleteLoan, updateLoanStatus } from '../services/loanService';
import { useTranslation } from 'react-i18next';
import LoanForm from '../components/loan/LoanForm';
import LoanStatusBadge from '../components/loan/LoanStatusBadge';
import Page from '../components/layout/Page';
import styled from 'styled-components';
import { spacing } from '../theme/tokens/spacing';
import { formatCurrency } from '../utils/formatters';
import DeleteConfirmationModal from '../components/loan/DeleteConfirmationModal';

const ActionSpace = styled(Space)`
  margin-left: ${spacing.md};
`;

const StyledCard = styled(Card)`
  margin-bottom: ${spacing.lg};
`;

// Helper function to format percentage
const formatPercentage = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return `${Number(value).toFixed(2)}%`;
};

// Helper function to safely format currency
const safeCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return formatCurrency(value);
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const getWithdrawalEndDate = (signatureDate) => {
  if (!signatureDate) return '';
  const date = new Date(signatureDate);
  date.setDate(date.getDate() + 14);
  return formatDate(date);
};

const getLoanStatusStep = (status) => {
  const statusMap = {
    'LoanStatus.PENDING': 0,
    'LoanStatus.IN_WITHDRAWAL': 1,
    'LoanStatus.WITHDRAWAL_PASSED': 2,
    'LoanStatus.PAYMENT_CONFIRMED': 3,
    'LoanStatus.PAID': 4,
    'LoanStatus.CONSULTANT_BILLED': 5,
  };
  return statusMap[status] || 0;
};

const LoanDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loan, setLoan] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  console.log('Debug LoanDetailsPage:', {
    id,
    locationSearch: location.search,
    timestamp: searchParams.get('ts'),
    locationKey: location.key
  });

  useEffect(() => {
    loadLoanDetails();
  }, [id, searchParams.get('ts')]);

  const loadLoanDetails = async () => {
    try {
      setLoading(true);
      const data = await getDetailedLoanInfo(id);
      setLoan(data);
    } catch (error) {
      console.error('Error loading loan:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/loans/${id}/edit`);
  };

  const handleDelete = async () => {
    try {
      await deleteLoan(id);
      message.success(t('loan.delete.success'));
      navigate('/loans');
    } catch (error) {
      message.error(t('loan.delete.error'));
    }
  };

  const handleCancel = async () => {
    console.log('handleCancel called for loan:', id);
    try {
      console.log('Attempting to update loan status to CANCELLED');
      const updatedLoan = await updateLoanStatus(id, 'LoanStatus.CANCELLED');
      console.log('Received updated loan:', updatedLoan);
      
      setLoan(updatedLoan);
      console.log('Loan state updated:', updatedLoan);
      
      message.success(t('loan.cancel.success'));
    } catch (error) {
      console.error('Error in handleCancel:', {
        error,
        loanId: id,
        message: error.message,
        response: error.response?.data
      });
      message.error(t('loan.cancel.error'));
    }
  };

  if (loading) {
    return (
      <Page
      title={`${t('loan.details.title')}`}
      subtitle={""}
    >
        <Space align="center" size="large">
          <Spin size="large" />
          <span>{t('common.loading')}</span>
        </Space>
      </Page>
    );
  }

  if (!loan) {
    return (
      <Page
      title={`${t('loan.details.title')}`}
      subtitle={""}
    >
        <div>{t('loan.notFound')}</div>
      </Page>
    );
  }

  const titleExtra = (
    <Space>

      {/* <Button
        icon={<StopOutlined />}
        onClick={handleCancel}
        disabled={loan?.status === 'LoanStatus.CANCELLED'}
      >
        {t('common.cancel')}
      </Button> */}
      <Button 
        danger 
        icon={<DeleteOutlined />} 
        onClick={() => setIsDeleteModalVisible(true)}
      >
        {t('common.delete')}
      </Button>
      <Button 
        icon={<EditOutlined />} 
        onClick={handleEdit}
      >
        {t('common.edit')}
      </Button>      
    </Space>
  );

  return (
    <Page
      title={`${t('loan.details.title')}: ${loan.process_number}`}
      subtitle={`${t('loan.fields.status')}: ${loan.status ? t(`loan.status.consultant.${loan.status.replace('LoanStatus.', '')}`) : '-'}`}
      headerExtra={titleExtra}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Status Timeline */}
        <StyledCard>
          <Steps
            current={getLoanStatusStep(loan.status)}
            items={[
              {
                title: t('loan.status.default.PENDING'),
                description: formatDate(loan.reported_at),
              },                
              {
                title: t('loan.status.default.IN_WITHDRAWAL'),
                description: formatDate(loan.signature_date),
              },
              {
                title: t('loan.status.default.WITHDRAWAL_PASSED'),
                description: loan.signature_date ? getWithdrawalEndDate(loan.signature_date) : '',
              },                
              {
                title: t('loan.status.default.PAYMENT_CONFIRMED'),
                description: formatDate(loan.statement_received_date || loan.invoice_date),
              },
              {
                title: t('loan.status.default.PAID'),
                description: formatDate(loan.payment_date),
              },
              {
                title: t('loan.status.default.CONSULTANT_BILLED'),
                description: formatDate(loan.consultant_billed_date),
              },
            ]}
          />
        </StyledCard>

        {/* Basic Information */}
        <StyledCard title={t('loan.details.basicInfo')}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={8}>
              <div>
                <strong>{t('loan.fields.customer')}</strong>
                <div>{loan.customer?.name}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.fields.amount')}</strong>
                <div>{formatCurrency(loan.amount)}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.fields.loanType')}</strong>
                <div>{loan.loan_type?.name}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.fields.bank')}</strong>
                <div>{loan.bank?.name}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.fields.pooler')}</strong>
                <div>{loan.pooler?.name}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.fields.interestLock')}</strong>
                <div>{loan.interest_lock_in_years} {t('common.years')}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.fields.referredBy')}</strong>
                <div>{loan.referred_by?.name || '-'}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.fields.signatureDate')}</strong>
                <div>{loan.signature_date ? formatDate(loan.signature_date) : '-'}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.fields.status')}</strong>
                <div>{loan.status ? t(`loan.status.consultant.${loan.status.replace('LoanStatus.', '')}`) : '-'}</div>
              </div>
            </Col>
          </Row>
        </StyledCard>

        {/* Commission Information */}
        <StyledCard title={t('loan.details.commission')}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={12}>
              <div>
                <strong>{t('loan.fields.provision')}</strong>
                <div>{formatPercentage(loan.provision)}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <div>
                <strong>{t('loan.fields.evaluationAmount')}</strong>
                <div>{safeCurrency(loan.evaluation_amount)}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <div>
                <strong>{t('loan.fields.primaryConsultant')}</strong>
                <div>{loan.primary_consultant?.first_name + ' ' + loan.primary_consultant?.last_name}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <div>
                <strong>{t('loan.fields.temporaryConsultant')}</strong>
                <div>{loan.secondary_consultant ? 
                  `${loan.secondary_consultant.first_name || ''} ${loan.secondary_consultant.last_name || ''}`.trim() || '-' 
                  : '-'}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <div>
                <strong>{t('loan.fields.primaryShare')}</strong>
                <div>{safeCurrency(loan.primary_consultant_share)}  {loan.temporary_consultant_percentage ? `(${(100-loan.temporary_consultant_percentage)}%)` : ''}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <div>
                <strong>{t('loan.fields.temporaryShare')}</strong>
                <div>{safeCurrency(loan.temporary_consultant_share) || '-'}  {loan.temporary_consultant_percentage ? `(${loan.temporary_consultant_percentage}%)` : ''}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <div>
                <strong>{t('loan.fields.extraBonus')}</strong>
                <div>{loan.extra_bonus ? safeCurrency(loan.extra_bonus) : '-'}</div>
              </div>
            </Col>
          </Row>
        </StyledCard>

        {/* Payment Information */}
        <StyledCard title={t('loan.details.payment')}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('common.invoiceDate')}</strong>
                <div>{loan.invoice_date ? formatDate(loan.invoice_date) : '-'}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('common.invoiceNumber')}</strong>
                <div>{loan.invoice_number || '-'}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.fields.paymentDate')}</strong>
                <div>{loan.payment_date ? formatDate(loan.payment_date) : '-'}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('common.statementReceivedDate')}</strong>
                <div>{loan.statement_received_date ? formatDate(loan.statement_received_date) : '-'}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('common.statementReceivedNumber')}</strong>
                <div>{loan.statement_received_number || '-'}</div>
              </div>
            </Col>
          </Row>
        </StyledCard>

        {/* Billing Information */}
        <StyledCard title={t('loan.details.billing')}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.billing.consultantBilledDate')}</strong>
                <div>{loan.consultant_billed_date ? formatDate(loan.consultant_billed_date) : '-'}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.billing.consultantBilledNumber')}</strong>
                <div>{loan.consultant_billed_number || '-'}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.billing.temporaryBilledDate')}</strong>
                <div>{loan.temporary_billed_date ? formatDate(loan.temporary_billed_date) : '-'}</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div>
                <strong>{t('loan.billing.temporaryBilledNumber')}</strong>
                <div>{loan.temporary_billed_number || '-'}</div>
              </div>
            </Col>
            <Col xs={24}>
              <div>
                <strong>{t('loan.billing.invoiceComment')}</strong>
                <div>{loan.invoice_comment || '-'}</div>
              </div>
            </Col>
          </Row>
        </StyledCard>
      </Space>

      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={() => setIsDeleteModalVisible(false)}
        onConfirm={handleDelete}
      />
    </Page>
  );
};

export default LoanDetailsPage;
